import classnames from 'classnames';
import React, { useRef, useState } from 'react';
import { Dropdown, Form, InputGroup } from 'react-bootstrap';

import './hui-input.scss';
import { HUIInputProps } from './types';

const HUIInput: React.FC<HUIInputProps> = ({ className, metadata, ...otherProps }) => {
  const { prefix, suffix, suggestions, onInputChange, hasError, title, helpText, value, ...inputProps } = otherProps;
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleSelectSuggestion = (event: React.MouseEvent<HTMLDivElement>) => {
    onInputChange(String(event.currentTarget.dataset.value));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange(event.target.value);
    if (inputProps.onChange) {
      inputProps.onChange(event);
    }
  };

  const handleInputFocus = (event) => {
    setIsFocused(true);
    if (inputProps.onFocus) {
      inputProps.onFocus(event);
    }
  };

  const handleInputBlur = (event) => {
    setIsFocused(false);
    if (inputProps.onBlur) {
      inputProps.onBlur(event);
    }
  };

  const handleDropdownIconClick = () => inputRef.current && inputRef.current.focus();

  const dropDownIcon = (
    <i className={classnames('pgicon-expand', { 'is-focused': isFocused })} onClick={handleDropdownIconClick} />
  );
  const updatedSuffix = suggestions ? dropDownIcon : suffix;

  const inputGroup = (
    <>
      {title && <Form.Label className="hui-input-title mb-3">{title}</Form.Label>}
      <InputGroup
        className={classnames('hui-input', className, {
          error: hasError,
          'has-suggestions': suggestions,
        })}
        {...metadata}
      >
        {prefix && <InputGroup.Text className="hui-input-prefix px-2 py-0">{prefix}</InputGroup.Text>}
        <Form.Control
          {...inputProps}
          ref={inputRef}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          value={value}
          className={classnames({
            'pe-0 border-right-0': updatedSuffix,
            'ps-0 border-left-0': prefix,
          })}
        />
        {updatedSuffix && (
          <InputGroup.Text
            className={classnames('hui-input-suffix p-0 pe-3', {
              'pe-3 ms-1': suggestions,
            })}
          >
            {updatedSuffix}
          </InputGroup.Text>
        )}
      </InputGroup>
    </>
  );

  return (
    <div className="hui-input-root">
      {suggestions && suggestions.length > 0 ? (
        <Dropdown show={isFocused}>
          {inputGroup}
          <Dropdown.Menu className="hui-input-suggestions">
            {suggestions.map((option) => (
              <Dropdown.Item key={option.value} data-value={option.value} onMouseDown={handleSelectSuggestion}>
                {option.text}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      ) : (
        inputGroup
      )}
      {helpText && <Form.Label className="hui-input-helptext mt-3">{helpText}</Form.Label>}
    </div>
  );
};

HUIInput.displayName = 'HUIInput';

export default HUIInput;
